import React, {useState} from 'react';
import {View, Text, TouchableOpacity} from 'react-native';
import {MaterialIcons} from '@expo/vector-icons';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import useLayoutTier from './useLayoutTier';
import styles, {
  chevronSize,
  chevronColor,
  hiddenStyle
} from '../../../style/adminTools';

const CollapsibleGroup = ({
  title,
  description,
  count,
  countLabel,
  badge,
  expanded,
  defaultExpanded = false,
  dimmed = false,
  errorCount = 0,
  band = 'customer',
  onToggle,
  children,
  testID
}) => {
  const phone = useLayoutTier() === 'mobile';
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isControlled = expanded !== undefined;
  const isOpen = isControlled ? expanded : internalExpanded;

  const toggle = () => {
    const next = !isOpen;
    if (!isControlled) setInternalExpanded(next);
    if (onToggle) onToggle(next);
  };

  const openStyle =
    band === 'team' ? styles.groupCardOpenTeam : styles.groupCardOpenCustomer;

  return (
    <View
      style={[
        styles.groupCard,
        isOpen ? openStyle : null,
        dimmed ? styles.groupCardDimmed : null
      ]}
      testID={testID}
    >
      <TouchableOpacity
        style={[styles.groupHeader, phone ? styles.groupHeaderPhone : null]}
        onPress={toggle}
        accessibilityRole="button"
        accessibilityState={{expanded: isOpen}}
        testID={testID ? `${testID}-header` : undefined}
      >
        <MaterialIcons
          name={isOpen ? 'expand-more' : 'chevron-right'}
          size={chevronSize}
          color={chevronColor}
        />
        <View
          style={
            phone ? styles.groupHeaderTextWrapPhone : styles.groupHeaderTextWrap
          }
          testID={testID ? `${testID}-title` : undefined}
        >
          <Text style={styles.groupTitle}>{title}</Text>
          {description && !phone ? (
            <Text style={styles.groupDescription}>{description}</Text>
          ) : null}
        </View>
        {badge ? (
          <View
            style={styles.groupBadge}
            testID={testID ? `${testID}-chip` : undefined}
          >
            <Text style={styles.groupBadgeText} numberOfLines={1}>
              {badge}
            </Text>
          </View>
        ) : null}
        {errorCount > 0 ? (
          <Text
            style={styles.groupErrorText}
            testID={testID ? `${testID}-errors` : undefined}
          >
            {I18n.t('adminTools.groupErrors', {count: errorCount})}
          </Text>
        ) : null}
        {countLabel || (count !== undefined && count !== null) ? (
          <View
            style={styles.groupBadge}
            testID={testID ? `${testID}-badge` : undefined}
          >
            <Text
              style={styles.groupBadgeText}
              numberOfLines={1}
              testID={testID ? `${testID}-count` : undefined}
            >
              {countLabel || count}
            </Text>
          </View>
        ) : null}
      </TouchableOpacity>
      <View
        style={isOpen ? styles.groupBody : hiddenStyle}
        testID={testID ? `${testID}-body` : undefined}
      >
        {children}
      </View>
    </View>
  );
};

CollapsibleGroup.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  count: PropTypes.number,
  countLabel: PropTypes.string,
  badge: PropTypes.string,
  expanded: PropTypes.bool,
  defaultExpanded: PropTypes.bool,
  dimmed: PropTypes.bool,
  errorCount: PropTypes.number,
  band: PropTypes.oneOf(['team', 'customer']),
  onToggle: PropTypes.func,
  children: PropTypes.node,
  testID: PropTypes.string
};

export default CollapsibleGroup;
